import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { getEmployeeAuditLog } from '@/lib/employees';
import { historyPreview } from '@/features/people/lib/employee-history';
import type { EmployeeAuditField } from '@/types/employee-audit';
import { employeesKeys } from './use-employees';

const PAGE_SIZE = 50;
const PREVIEW_FETCH_SIZE = 20;

/**
 * The History tab's feed. Keyed under the employee's detail key, so every
 * save that invalidates the profile refreshes the history too. [HW-795]
 */
export function useEmployeeAuditLog(
  id: string,
  field: EmployeeAuditField | 'all',
  opts: { enabled?: boolean } = {}
) {
  return useInfiniteQuery({
    queryKey: [...employeesKeys.detail(id), 'audit-log', field] as const,
    queryFn: ({ pageParam }) =>
      getEmployeeAuditLog(id, {
        limit: PAGE_SIZE,
        cursor: pageParam,
        field: field === 'all' ? undefined : field,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: !!id && (opts.enabled ?? true),
    // A change saved from the card header while this tab is open must show
    // on the next mount, not after the default cache window.
    staleTime: 0,
  });
}

/**
 * The Job tab's three newest changes. Pass `enabled: false` without
 * employees.audit.view: the request would 403. [HW-795]
 *
 * [HW-1019] It has its own small, non-infinite query instead of reading the
 * History tab's feed: that feed holds every page the user scrolled through, and
 * each refetch of its key (opening Job, any save) would re-request all of them
 * for three rows. Keyed under the detail key, so saves still refresh it.
 * Accepted limit: the preview takes the server's newest 20 rows (createdAt desc,
 * then id desc, and ids are random). A save whose rows straddle the 20th row can
 * lose some of them from the preview, and its visible rows may then rank
 * differently here than in the History tab.
 */
export function useEmployeeAuditPreview(id: string, opts: { enabled?: boolean } = {}) {
  const query = useQuery({
    queryKey: [...employeesKeys.detail(id), 'audit-log-preview'] as const,
    queryFn: () => getEmployeeAuditLog(id, { limit: PREVIEW_FETCH_SIZE }),
    enabled: !!id && (opts.enabled ?? true),
    staleTime: 0,
  });
  const preview = historyPreview(query.data?.data ?? [], query.data?.nextCursor != null);
  return {
    entries: preview.entries,
    hasMore: preview.hasMore,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    isRefetchError: query.isRefetchError,
    isRefetching: query.isRefetching,
  };
}
